import sortBy from 'lodash-es/sortBy';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { ProductApiProductsRecommendedListRequest, RecommendedProduct, Sponsorship } from '@/clients/unifiedApi';
import { SPLIT_NAMES } from '@/constants';
import { BUYER_DASHBOARD_AD_PLACEMENTS, SELLER_DASHBOARD_AD_PLACEMENTS } from '@/constants/sponsorship';
import { SegmentAdContentTrackingData, SegmentAdTrackingData, SegmentProductTrackingData } from '@/models/segment';
import { useShopStore } from '@/piniaStores/shop/shop.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import { trackAdClick, trackAdImpression } from '@/segment';
import {
  getProductsRecommendedList,
  getSponsoredDashboardContent,
  reportSponsoredContentClickToProvider,
} from '@/services/api/content';

interface UseSponsorshipOptions {
  isBuyer: boolean;
}
/**
 * Regardless of the target resource name, the ProductSpotlight is designed to track both the brand and product IDs.
 */
interface SponsorshipTrackingDataOverrides {
  brand_id?: number;
  product_id?: number;
  cta_clicked?: string; // TODO: use CTA_CLICKED enum to define the correct type values
}

interface ProductTrackingDataOverrides extends SponsorshipTrackingDataOverrides {
  placement_name?: string;
  cta_clicked?: string;
}

export const useSponsorship = (companyId: string | number | undefined, { isBuyer }: UseSponsorshipOptions) => {
  /**
   * Ads displayed in the carousel for buyer and seller dashboards
   */
  const dashboardCarouselAds = ref<Sponsorship[]>([]);

  const recommendedProducts = ref<RecommendedProduct[]>([]);

  const { getIsSplitEnabled: isSplitEnabled } = storeToRefs(useUserStore());

  const { trackImpression } = useShopStore();

  const isAdFromMoloco = (provider: string | undefined) =>
    provider && ['moloco', 'moloco_product', 'moloco_skyscraper', 'moloco_featured_brand_listing'].includes(provider);

  /**
   * Maps sponsorship data to Segment tracking data
   *
   * The override modifies mapSegmentTrackingData data without impacting its current use in other components.
   */
  const mapSegmentTrackingData = (
    sponsorship: Sponsorship,
    overrides: SponsorshipTrackingDataOverrides = {},
  ): SegmentAdTrackingData => {
    // Determine brand and product IDs based on the target resource name, potentially will have other things in the future
    const brandId = sponsorship.target_resource_name === 'brand' ? Number(sponsorship.target_resource_id) : null;
    const productId = sponsorship.target_resource_name === 'product' ? Number(sponsorship.target_resource_id) : null;

    // @ts-expect-error Property 'id' does not exist on type 'AdPlacement'
    // this is temporary and will be updated when the BE change to use
    // data.placement.id is fully completed.
    const placementName = sponsorship.placement?.id || sponsorship.placement?.location?.id;

    return {
      adzerk_ad_id: sponsorship.ad_id,
      adzerk_campaign_id: sponsorship.campaign_id,
      adzerk_creative_id: sponsorship.creative.creative_id,
      brand_id: brandId,
      click_url: sponsorship.click_url,
      impression_url: sponsorship.impression_url,
      metadata: sponsorship.creative.meta_data,
      page_load_id: sponsorship.page_load_id,
      placement_name: placementName,
      product_id: productId,
      provider_id: sponsorship.provider_id,
      target_company_id: sponsorship.target_company_id,
      target_user_id: sponsorship.target_user_id,
      ...overrides,
    };
  };

  /**
   * Fetches sponsored carousel ads for the buyer or seller dashboard
   */
  const getDashboardCarouselAds = async () => {
    if (!companyId) {
      return;
    }

    // @ts-expect-error Property 'content' does not exist on type 'SponsorshipsContent'.
    // TODO - remove this when the BE team updates logic to match what the spec defines.
    // Ads content is indeed part of the content object destructured here.
    const { content } = await getSponsoredDashboardContent({
      companyId: Number(companyId),
      placement: isBuyer ? BUYER_DASHBOARD_AD_PLACEMENTS : SELLER_DASHBOARD_AD_PLACEMENTS,
    });

    const sortedCarouselAds = sortBy(content.sponsorships?.banners || [], (banner) => {
      // temporary logic to be removed when we have coordinated the full
      // BE change to only use banner.sponsorship.placement.
      if ('id' in banner.sponsorship.placement) {
        return banner.sponsorship.placement.id;
      } else {
        banner.sponsorship.placement.location.id;
      }
    });

    dashboardCarouselAds.value = sortedCarouselAds.map(({ sponsorship }) => sponsorship);
  };

  const refreshRecommendedProducts = async (payload: Omit<ProductApiProductsRecommendedListRequest, 'companyId'>) => {
    if (!companyId) {
      return;
    }

    const { results } = await getProductsRecommendedList({
      ...payload,
      companyId: Number(companyId),
    });

    recommendedProducts.value = results || [];
  };

  /**
   * Reports ad clicks to Segment and the ad provider, if the clickReportingUrl is provided.
   */
  const reportSponsorshipClick = async (
    clickReportingUrl: string | undefined, // can be an empty string if value is passed through directly from the API response
    segmentTrackingData: SegmentAdTrackingData | SegmentAdContentTrackingData,
  ) => {
    // report the click to Segment
    trackAdClick(segmentTrackingData);

    // Segment will soon be responsible for reporting clicks directly to the ad provider,
    // so a flag was created to allow us to disable this logic when we're ready to make the switch.
    // Logic will be removed from the FE in https://leaflink.atlassian.net/browse/ADS-1197 afterwards
    if (clickReportingUrl && isSplitEnabled.value(SPLIT_NAMES.ADS_ENABLE_MOLOCO_AD_REPORTING)) {
      await reportSponsoredContentClickToProvider(clickReportingUrl);
    }
  };

  /**
   * Reports ad impressions to Segment and the ad provider
   */
  const reportSponsorshipImpression = async (
    impressionReportingUrl: string | undefined,
    segmentTrackingData: SegmentAdTrackingData | SegmentAdContentTrackingData | undefined,
  ) => {
    // report the impression to Segment
    trackAdImpression(segmentTrackingData);

    // impressions are reported directly to the ad provider under the follow conditions:
    //  1. the ad was NOT served by Moloco, or
    //  2. the ad was served by Moloco, AND the ads_pod_enable_moloco_ad_reporting split is enabled
    const preventMolocoReporting =
      isAdFromMoloco(segmentTrackingData?.provider_id) &&
      !isSplitEnabled.value(SPLIT_NAMES.ADS_ENABLE_MOLOCO_AD_REPORTING);

    if (preventMolocoReporting || !impressionReportingUrl) {
      return;
    }

    await trackImpression(impressionReportingUrl);
  };

  const mapProductTrackingData = (
    product: RecommendedProduct,
    overrides: ProductTrackingDataOverrides = {},
  ): SegmentProductTrackingData => {
    return {
      brand_id: product.brand.id,
      product_id: product.id,
      target_company_id: Number(product.company_id),
      placement_name: '',
      ...overrides,
    };
  };

  return {
    dashboardCarouselAds,
    getDashboardCarouselAds,
    isAdFromMoloco,
    mapSegmentTrackingData,
    mapProductTrackingData,
    recommendedProducts,
    refreshRecommendedProducts,
    reportSponsorshipClick,
    reportSponsorshipImpression,
  };
};
